import type { AnalysisStatisticsEntryId } from '@/teoCore/TeoApi';

export interface StatEntry<TId extends string = string, TSecondaryId extends string = never> {
  id: TId;

  /** optional, falls der wert aus zwei werten besteht */
  secondaryId?: TSecondaryId;

  /** format of the number. default is int */
  type?: 'int' | 'float-2' | 'percentage';
  label: string;
  evaluations?: Evaluation[];

  /** zur Filterung, der Beurteilung. Wird nur angezeigt, wenn der index "used" ist */
  evalIndex?: 'comprehensibility' | 'abstractness' | 'activation' | 'personal';
}

export type EvaluationType = 'good' | 'warn' | 'bad' | 'neutral';
export interface Evaluation {
  max?: number;
  type: EvaluationType;
  label: string;
}

export const StatisticsTableDefinition = [
  {
    id: 'num_words',
    label: 'Anzahl Wörter',
  },
  {
    id: 'word_length',
    label: 'Ø Wortlänge',
    type: 'float-2',
    evalIndex: 'comprehensibility',
    evaluations: [
      {
        max: 2,
        type: 'good',
        label: 'im optimalen Bereich',
      },
      {
        max: 2.2,
        type: 'warn',
        label: 'im mittleren Bereich',
      },
      {
        type: 'bad',
        label: 'im kritischen Bereich',
      },
    ],
  },
  {
    id: 'num_long_words',
    label: 'Anzahl lange Wörter',
  },
  {
    id: 'percent_long_words',
    type: 'percentage',
    label: 'Lange Wörter in %',
  },
  {
    id: 'num_sentences',
    label: 'Anzahl Sätze',
  },
  {
    id: 'sentence_length',
    label: 'Ø Satzlänge',
    type: 'float-2',
    evalIndex: 'comprehensibility',
    evaluations: [
      {
        max: 14.0,
        type: 'good',
        label: 'im optimalen Bereich',
      },
      {
        max: 20.0,
        type: 'warn',
        label: 'im mittleren Bereich',
      },
      {
        type: 'bad',
        label: 'im kritischen Bereich',
      },
    ],
  },
  {
    id: 'num_long_sentences',
    label: 'Anzahl lange Sätze',
  },
  {
    id: 'percent_long_sentences',
    label: 'Lange Sätze in %',
    type: 'percentage',
  },
  {
    id: 'num_substantives',
    label: 'Anzahl Substantive',
  },
  {
    id: 'percent_substantives',
    label: 'Anteil Substantive in %',
    type: 'percentage',
    evalIndex: 'abstractness',
    evaluations: [
      {
        max: 20,
        type: 'good',
        label: 'im optimalen Bereich',
      },
      {
        max: 25,
        type: 'warn',
        label: 'leicht erhöht, Tendenz zum Nominalstil',
      },
      {
        type: 'bad',
        label: 'im kritischen Bereich: Nominal-Stil (negativer Einfluss auf Tonalität)',
      },
    ],
  },
  {
    id: 'num_abstracts',
    label: 'Anzahl abstr. Substantive',
  },
  {
    id: 'percent_abstracts',
    label: 'Anteil abstr. Substantive in %',
    type: 'percentage',
    evalIndex: 'abstractness',
    evaluations: [
      {
        max: 10,
        type: 'good',
        label: 'im optimalen Bereich',
      },
      {
        max: 20,
        type: 'warn',
        label: 'im mittleren Bereich',
      },
      {
        type: 'bad',
        label: 'im kritischen Bereich',
      },
    ],
  },
  {
    id: 'num_personal_sentences',
    label: 'Anzahl persönliche Sätze',
  },
  {
    id: 'num_personal_words',
    label: 'Anzahl persönliche Wörter',
  },
  {
    id: 'percent_we',
    secondaryId: 'percent_you',
    label: 'Perspektive: Wir% – Sie%',
    type: 'percentage',
    evalIndex: 'personal',
    evaluations: [
      {
        max: 40,
        type: 'good',
        label: 'sehr gut ausgeprägt',
      },
      {
        max: 60,
        type: 'warn',
        label: 'Wir-Perspektive ausgeprägt, Tendenz zur Innensicht',
      },
      {
        type: 'bad',
        label: 'Wir-Perspektive überwiegt (Gefahr: zu wenig Leser-Vorteile)',
      },
    ],
  },
  {
    id: 'num_impuls_sentences',
    label: 'Anzahl Sätze mit Impuls',
  },
  {
    id: 'impuls_weight',
    label: 'Impulskraft Gesamt',
    type: 'float-2',
  },
  {
    id: 'impuls_weight_per_sentence',
    label: 'Ø Impulskraft pro Satz',
    type: 'float-2',
    evalIndex: 'activation',
    // this is analysis activation based
    evaluations: [
      {
        max: 50,
        type: 'bad',
        label: 'im kritischen Bereich (zu wenig oder zu viel Aktivierung)',
      },
      {
        max: 80,
        type: 'warn',
        label: 'im mittleren Bereich',
      },
      {
        type: 'good',
        label: 'sehr gut ausgeprägt',
      },
    ],
  },
] as const satisfies StatEntry<AnalysisStatisticsEntryId, AnalysisStatisticsEntryId>[];
